import { useEffect, useMemo, useState } from 'react';

import {
  useSubscriptionSwitchOptions,
  useSwitchSubscriptionProduct,
} from '@/lib/api/clinicalAdminHooks';
import type { StripeSubscription } from '@/types/clinicalAdmin';

import { fmtDate, fmtMoney } from './ui';

export function SwitchSubscriptionFormatModal({
  patientId,
  subscription,
  onClose,
}: {
  patientId: string;
  subscription: StripeSubscription;
  onClose: () => void;
}) {
  const optionsQuery = useSubscriptionSwitchOptions(patientId, subscription.id);
  const switchProduct = useSwitchSubscriptionProduct(patientId);
  const options = useMemo(
    () =>
      (optionsQuery.data?.options ?? []).filter((option) => option.eligible),
    [optionsQuery.data],
  );
  const blocked = useMemo(
    () =>
      (optionsQuery.data?.options ?? []).filter((option) => !option.eligible),
    [optionsQuery.data],
  );
  const [newProductId, setNewProductId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!newProductId && options[0]) setNewProductId(options[0].to_product_id);
  }, [newProductId, options]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !switchProduct.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, switchProduct.isPending]);

  const selected = options.find(
    (option) => option.to_product_id === newProductId,
  );
  const submit = () => {
    if (!selected || !note.trim()) return;
    if (
      !window.confirm(
        `Change future renewals from ${selected.from_product_name ?? subscription.product_name ?? 'the current format'} to ${selected.to_product_name}?\n\n` +
          `Nothing is billed today and the current shipment is unchanged. The new format begins ${fmtDate(selected.effective_at)}.`,
      )
    )
      return;
    switchProduct.mutate({
      subId: subscription.id,
      newProductId: selected.to_product_id,
      note: note.trim(),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
      onClick={() => {
        if (!switchProduct.isPending) onClose();
      }}
    >
      <div
        className="bg-white border border-default shadow-xl w-full max-w-content-md flex flex-col gap-16 p-15 my-24"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-lg">Switch next renewal</h2>
            <p className="type-body-sm text-ink-muted">
              {subscription.product_name || subscription.id}
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={onClose}
            disabled={switchProduct.isPending}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {switchProduct.data ? (
          <div className="flex flex-col gap-12">
            <div className="border border-default bg-sunken p-12 type-body-sm">
              Future renewals now use {switchProduct.data.to_product}. The next
              renewal is {fmtDate(switchProduct.data.effective_at)} at{' '}
              {fmtMoney(switchProduct.data.new_recurring_cents, 'usd')}. Nothing
              was billed today and the current shipment was not changed.
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="border border-accent bg-accent-wash p-12 type-body-sm">
              This changes the subscription only at its next renewal. It does
              not cancel, recall, replace, or reprice the shipment already in
              progress, and no prorated charge is created today.
            </div>

            {optionsQuery.isLoading ? (
              <p className="type-body-sm text-ink-muted">
                Loading eligible formats…
              </p>
            ) : optionsQuery.isError ? (
              <p className="type-body-sm text-danger">
                Could not load format options. Try again.
              </p>
            ) : options.length === 0 ? (
              <div className="flex flex-col gap-4">
                <p className="type-body-sm">
                  No next-renewal format switch is available for this
                  subscription.
                </p>
                {blocked.length > 0 && (
                  <p className="type-10 text-ink-muted">
                    The available catalog alternatives are currently blocked by
                    subscription, fulfillment, or product eligibility rules.
                  </p>
                )}
              </div>
            ) : (
              <>
                <label className="flex flex-col gap-4">
                  <span className="type-label-md">New format</span>
                  <select
                    className="input"
                    value={newProductId}
                    onChange={(event) => setNewProductId(event.target.value)}
                    disabled={switchProduct.isPending}
                  >
                    {options.map((option) => (
                      <option
                        key={option.to_product_id}
                        value={option.to_product_id}
                      >
                        {option.to_product_name}
                      </option>
                    ))}
                  </select>
                </label>
                {selected && (
                  <div className="grid grid-cols-2 gap-8 border border-line-subtle p-12 type-body-sm">
                    <div>
                      <div className="type-10 text-ink-muted">EFFECTIVE</div>
                      {fmtDate(selected.effective_at)}
                    </div>
                    <div>
                      <div className="type-10 text-ink-muted">
                        NEW RECURRING PRICE
                      </div>
                      {fmtMoney(selected.new_recurring_cents ?? null, 'usd')}
                    </div>
                    <div className="col-span-2 type-10 text-ink-muted">
                      Due today: $0.00 · current supply unchanged
                    </div>
                  </div>
                )}
                <label className="flex flex-col gap-4">
                  <span className="type-label-md">
                    Reason / patient request
                  </span>
                  <textarea
                    className="input min-h-[88px]"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={500}
                    placeholder="Required for the subscription audit trail"
                    disabled={switchProduct.isPending}
                  />
                </label>
              </>
            )}

            {switchProduct.isError && (
              <p className="type-body-sm text-danger">
                {(switchProduct.error as Error)?.message || 'Switch failed.'}
              </p>
            )}
            <div className="flex items-center justify-end gap-8">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={onClose}
                disabled={switchProduct.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={submit}
                disabled={!selected || !note.trim() || switchProduct.isPending}
              >
                {switchProduct.isPending ? 'Switching…' : 'Switch next renewal'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
